import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip
} from '@mui/material';
import { Search, Person, Email } from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { fetchUsers } from '../../services/userService';
import { usePeopleAdmin } from '../../context/PeopleAdminContext';
import * as logger from 'utils/logger';

/**
 * Dialog component for linking a user to an employee
 * @param {Object} props - Component props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Handler to close dialog
 * @param {Function} props.onConfirm - Handler when user is selected
 * @param {Object} props.employee - Employee to link user to
 * @param {boolean} props.loading - Loading state for the link operation
 * @returns {React.Component} User selection dialog
 */
function UserLinkDialog({
  open,
  onClose,
  onConfirm,
  employee,
  loading = false
}) {
  const { clientId } = usePeopleAdmin();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch available users when dialog opens
  useEffect(() => {
    if (open && clientId) {
      loadUsers();
    }
  }, [open, clientId]);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = users.filter(user => {
        const email = (user.email || '').toLowerCase();
        const username = (user.username || '').toLowerCase();
        const name = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
        return email.includes(term) || username.includes(term) || name.includes(term);
      });
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const allUsers = await fetchUsers(clientId);
      
      // Filter out users that are already linked to an employee
      // Users with employeeId set are already linked
      const availableUsers = allUsers.filter(user => !user.employeeId);
      
      setUsers(availableUsers);
      setFilteredUsers(availableUsers);
      
      if (availableUsers.length === 0) {
        setError('No available users to link. All users are already linked to employees.');
      }
    } catch (err) {
      logger.error('Failed to load users for linking', { error: err });
      setError('Failed to load users. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedUserId && onConfirm) {
      const selectedUser = users.find(u => u.id === selectedUserId);
      onConfirm(selectedUser);
    }
  };

  const handleClose = () => {
    setSelectedUserId(null);
    setSearchTerm('');
    setError(null);
    onClose();
  };

  const getUserDisplayName = (user) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.username || user.email || 'Unknown User';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Link User to Employee
        </Typography>
        {employee && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            Select a RadixDash user to link to {employee.firstName} {employee.lastName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!error && (
          <>
            {/* Search Field */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{ mb: 2 }}
              disabled={isLoading}
            />

            {/* User List */}
            {isLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : filteredUsers.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  {searchTerm ? 'No users found matching your search' : 'No available users to link'}
                </Typography>
              </Box>
            ) : (
              <RadioGroup
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {filteredUsers.map((user) => (
                    <ListItem
                      key={user.id}
                      disablePadding
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <ListItemButton
                        onClick={() => setSelectedUserId(user.id)}
                        selected={selectedUserId === user.id}
                      >
                        <FormControlLabel
                          value={user.id}
                          control={<Radio />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Person sx={{ fontSize: 18, color: 'primary.main' }} />
                                <Typography variant="body2" fontWeight="medium">
                                  {getUserDisplayName(user)}
                                </Typography>
                                {user.role && (
                                  <Chip
                                    label={user.role}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Box>
                              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {user.email || user.username || 'No email'}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </RadioGroup>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <AdminButton
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </AdminButton>
        <AdminButton
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedUserId || loading || isLoading}
        >
          {loading ? <CircularProgress size={20} /> : 'Link User'}
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
}

UserLinkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  employee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired
  }),
  loading: PropTypes.bool
};

export default UserLinkDialog;